/** @format */
import { ChangeDetectionStrategy, Component, computed, Inject, inject, input, ViewEncapsulation } from '@angular/core';
import { CreditRange, DegreeProgramme, GroupingModule, Module, PlanValidationState, StudyModule } from 'common-typescript/types';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../plan/plan-actions-service/plan-actions.service';
import { ModuleStateObject } from '../../service/plan-state.service';
import { PlanStructureContext } from '../plan-structure-context';
import { getFudisHeadingSize } from '../plan-structure-utils';

@Component({
    selector: 'sis-plan-structure-module',
    templateUrl: './plan-structure-module.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureModuleComponent {
    readonly module = input.required<GroupingModule | StudyModule | DegreeProgramme>();
    readonly isImplicit = input<boolean>();
    readonly isParentModule = input<Module>();
    readonly headingLevel = input.required<number>();

    private readonly planStructureContext = inject(PlanStructureContext);

    protected readonly targetCredits = computed<CreditRange | undefined>(() => {
        const module = this.module();
        return module.type !== 'GroupingModule' ? module.targetCredits : undefined;
    });
    protected readonly studyLevel = computed(() => {
        const module = this.module();
        return module.type === 'StudyModule' ? module.studyLevel : undefined;
    });
    protected readonly moduleStateObject = computed<ModuleStateObject | undefined>(
        () => this.planStructureContext.planStateObject()?.modules?.[this.module().id],
    );
    /** Whether the selection state goes with the attained credits instead of the planned credits */
    protected readonly showsSelectionStateWithAttainedCredits = computed(() => {
        const moduleStateObject = this.moduleStateObject();
        return (
            !!moduleStateObject?.isAttained ||
            (moduleStateObject?.contentApprovalState === 'NONE' && moduleStateObject.validationState === PlanValidationState.PARTS_ATTAINED)
        );
    });

    constructor(@Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService) {}

    openModuleInfoModal(event?: MouseEvent) {
        event?.preventDefault();
        event?.stopPropagation();
        this.planActionsService.openModuleInfoModal(this.module());
    }

    openModule() {
        this.planActionsService.openModule(this.module());
    }

    isDegreeProgrammeModule() {
        return this.module().type === 'DegreeProgramme';
    }

    getHeadingSize() {
        return getFudisHeadingSize(this.headingLevel());
    }

    useBackgroundColor() {
        return this.module().type === 'StudyModule' || this.module().type === 'GroupingModule';
    }
}
